var STORAGE_KEY = 'alleflow:lang';
var SUPPORTED = ['en', 'da'];
var TEXT_PROPS = {
  alt: true,
  placeholder: true,
  title: true,
  'aria-label': true,
  'aria-description': true,
  'aria-valuetext': true
};
var danishStrings = require('../data/appStringsDa');
var activeLanguage = 'en';
var dynamicFragments = Object.keys(danishStrings)
  .filter(function(source) {
    return source.length >= 5 && (
      /^[.·]/.test(source)
      || (/^[a-z]/.test(source) && /[\s.:()[\]…—]/.test(source))
      || /[:(]$/.test(source)
    );
  })
  .sort(function(a, b) { return b.length - a.length; });

function isSupported(lang) {
  return SUPPORTED.indexOf(lang) !== -1;
}

function readStoredLanguage() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    var stored = window.localStorage.getItem(STORAGE_KEY);
    return isSupported(stored) ? stored : null;
  } catch (err) {
    return null;
  }
}

function detectBrowserLanguage() {
  if (typeof navigator === 'undefined') return 'en';
  var raw = (navigator.language || (navigator.languages && navigator.languages[0]) || 'en').toLowerCase();
  return raw.indexOf('da') === 0 ? 'da' : 'en';
}

function detectLanguage() {
  return readStoredLanguage() || detectBrowserLanguage();
}

function normalizeLanguage(lang) {
  return isSupported(lang) ? lang : 'en';
}

function setStoredLanguage(lang) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (!isSupported(lang)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch (err) {
    /* ignore */
  }
}

function setActiveLanguage(lang) {
  activeLanguage = normalizeLanguage(lang);
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = activeLanguage;
  }
}

function getActiveLanguage() {
  return activeLanguage;
}

function translateDynamicText(text) {
  var match = text.match(/^(\d+)\s+workflows?$/i);
  if (match) return match[1] + (match[1] === '1' ? ' workflow' : ' workflows');

  match = text.match(/^(\d+)\s+nodes?$/i);
  if (match) return match[1] + (match[1] === '1' ? ' knude' : ' knuder');

  match = text.match(/^(\d+)\s+(items|steps|fields|lines|tries|tokens|characters|chars|installs|accounts|variables)$/i);
  if (match) {
    var nouns = {
      items: 'elementer',
      steps: 'trin',
      fields: 'felter',
      lines: 'linjer',
      tries: 'forsøg',
      tokens: 'tokens',
      characters: 'tegn',
      chars: 'tegn',
      installs: 'installationer',
      accounts: 'konti',
      variables: 'variabler'
    };
    return match[1] + ' ' + nouns[match[2].toLowerCase()];
  }

  match = text.match(/^(.+)\s+ago$/i);
  if (match) return 'for ' + match[1] + ' siden';

  match = text.match(/^Delete "(.+)"\?$/);
  if (match) return 'Slet "' + match[1] + '"?';

  match = text.match(/^Delete (.+)$/);
  if (match) return 'Slet ' + match[1];

  match = text.match(/^Rename (.+)$/);
  if (match) return 'Omdøb ' + match[1];

  var translated = text;
  dynamicFragments.some(function(source) {
    var index = translated.indexOf(source);
    if (index < 0 || translated === source) return false;

    var startsWithPunctuation = /^[.·]/.test(source);
    var isPrefix = /[:(]$/.test(source) && index === 0;
    var isSuffix = /^[a-z]/.test(source) && index + source.length === translated.length;
    if (!startsWithPunctuation && !isPrefix && !isSuffix) return false;

    translated = translated.slice(0, index) + danishStrings[source] + translated.slice(index + source.length);
    return true;
  });
  return translated;
}

function translateText(value) {
  if (activeLanguage !== 'da' || typeof value !== 'string' || !value) return value;
  var leading = (value.match(/^\s*/) || [''])[0];
  var trailing = (value.match(/\s*$/) || [''])[0];
  var text = value.slice(leading.length, value.length - trailing.length);
  if (!text) return value;
  var translated = danishStrings[text] || translateDynamicText(text);
  return leading + translated + trailing;
}

function translateChild(child) {
  if (typeof child === 'string') return translateText(child);
  if (Array.isArray(child)) return child.map(translateChild);
  return child;
}

function translateProps(type, props) {
  if (!props || activeLanguage !== 'da') return props;
  var next = props;
  Object.keys(TEXT_PROPS).forEach(function(name) {
    if (typeof props[name] !== 'string') return;
    var translated = translateText(props[name]);
    if (translated === props[name]) return;
    if (next === props) next = Object.assign({}, props);
    next[name] = translated;
  });

  var inputType = type === 'input' ? String(props.type || '').toLowerCase() : '';
  if ((inputType === 'button' || inputType === 'submit' || inputType === 'reset') && typeof props.value === 'string') {
    var translatedValue = translateText(props.value);
    if (translatedValue !== props.value) {
      if (next === props) next = Object.assign({}, props);
      next.value = translatedValue;
    }
  }

  if (props.children !== undefined) {
    var translatedChildren = translateChild(props.children);
    if (translatedChildren !== props.children) {
      if (next === props) next = Object.assign({}, props);
      next.children = translatedChildren;
    }
  }
  return next;
}

function installReactTranslator() {
  var React = require('react');
  if (React.__alleflowTranslationInstalled) {
    setActiveLanguage(detectLanguage());
    return;
  }

  var originalCreateElement = React.createElement;
  React.createElement = function(type, props) {
    var args = Array.prototype.slice.call(arguments);
    args[1] = translateProps(type, props);
    for (var index = 2; index < args.length; index += 1) {
      args[index] = translateChild(args[index]);
    }
    return originalCreateElement.apply(React, args);
  };
  React.__alleflowTranslationInstalled = true;

  if (typeof window !== 'undefined' && !window.__alleflowTranslationInstalled) {
    ['alert', 'confirm', 'prompt'].forEach(function(name) {
      if (typeof window[name] !== 'function') return;
      var original = window[name].bind(window);
      window[name] = function(message) {
        var args = Array.prototype.slice.call(arguments);
        args[0] = translateText(message);
        return original.apply(window, args);
      };
    });
    window.__alleflowTranslationInstalled = true;
  }
  setActiveLanguage(detectLanguage());
}

module.exports = {
  detectLanguage: detectLanguage,
  getActiveLanguage: getActiveLanguage,
  installReactTranslator: installReactTranslator,
  setActiveLanguage: setActiveLanguage,
  setStoredLanguage: setStoredLanguage,
  translateText: translateText,
  SUPPORTED: SUPPORTED
};
